import { KitchenDesignBase } from '../kitchen-design-base';
import { Backsplash } from './backsplash.class';
import { Panel } from './panel.class';
import { panelsData } from './panels.data';
import { PanelsOptions } from './types/panels-options.type';
import { SurroundMaterial } from '../surround-material/surround-material.class';

import { BlueprintInstanceType, IslandType } from '../public.enums';
import { buildCornerIslandPanelsCfn, buildStandardIslandPanelsCfn } from './functions';
import { BacksplashDataIn, PanelsDataIn, PanelsDataOut } from './types';

export class Panels extends KitchenDesignBase<PanelsDataIn> {
	readonly children: Array<Panel>;
	readonly frontPanelId: string | null;
	#surroundMaterial: SurroundMaterial;

	/**
	 * The constructor of the 'Panels' class
	 * @param options provides context, also used to hydrate the instance
	 */
	constructor (options: PanelsOptions = {}) {
		super(BlueprintInstanceType.Panels, options);

		const { dataIn = null } = options;
		const data = panelsData(dataIn);
		this.children = [];
		this.frontPanelId = null;
		this.#surroundMaterial = new SurroundMaterial({ dataIn: data?.surroundMaterial ?? null, parent: this, syncTo: this.refMap?.kitchen?.material?.surround ?? null });

		if (data && (data.children?.length ?? 0) > 0) {
			data.children?.forEach((n) => {
				// Check if this is backsplash data by looking for backsplash-specific properties
				const isBacksplash = 'associatedPanelId' in n || n?.label?.startsWith('backsplash-');
				if (isBacksplash) {
					this.children.push(new Backsplash({ dataIn: n as BacksplashDataIn, parent: this }));
				} else {
					this.children.push(new Panel({ dataIn: n, parent: this }));
				}
			});
			// Use provided frontPanelId, or default to first child's id
			this.frontPanelId = data.frontPanelId ?? this.children[0]?.id ?? null;
		} else if (options.type) {
			switch (options.type) {
				case IslandType.Grill:
				case IslandType.Bar:
				case IslandType.Pizza:
				case IslandType.Smoker:
				case IslandType.Asado:
					this.frontPanelId = buildStandardIslandPanelsCfn(this);
					break;
				case IslandType.Corner:
					this.frontPanelId = buildCornerIslandPanelsCfn(this);
					break;
				default:
			}
		}

		this.setInitialized();
	}

	get surroundMaterial (): SurroundMaterial | null { return this.refMap.kitchen?.material?.surround ?? null; }

	/**
	 * A method to locate and return the front panel
	 * @returns instance of Panel or null
	 */
	public getFrontPanel (): Panel | null {
		return this.children.find((p) => p.id === this.frontPanelId) ?? null;
	}

	public getBackPanels (): Array<Panel> {
		return this.children.filter((p) => p.isBackPanel) ?? [];
	}

	public getBackPanelIds (): Array<string> {
		return this.getBackPanels().map((p) => p.id);
	}

	/**
	 * method to allows the consumer to call find insead of having to used children.find
	 * @param callback signature matched array.find()
	 * @returns instance if Panel or null
	 */
	public find (callback: (key: Panel) => boolean | undefined): Panel | null {
		return this.children.find(callback) ?? null;
	}

	/**
	 * A method to locate a panel using the panels id
	 * @param id identifier of the panel to locate
	 * @returns instance of found panel or null
	 */
	public findById (id: string | null): Panel | null {
		if (id === null) return null;
		return this.children.find((p) => p.id === id) ?? null;
	}

	/**
	 * Return the first panel to match the angle provided
	 * @param angle angle on Y to match
	 * @returns Panel
	 */
	public findPanelByAngle (angle: number): Panel | null {
		if (angle === null) return null;
		return this.children.find((p) => p.getOriginAngle() === angle) ?? null;
	}

	/**
	 *
	 * @returns serialized data representing class state for persistence
	 */
	public serialize ():PanelsDataOut {
		return {
			frontPanelId: this.frontPanelId,
			children: this.children.map((panel: Panel) => panel.serialize()),
			surroundMaterial: this.surroundMaterial?.serialize() ?? null
		};
	}
}
